import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Folder, FolderPlus, MessagesSquare, Edit2, Trash2, FileText, MoreHorizontal, Link2 } from 'lucide-react';
import { Folder as FolderType, Thread, FolderContextOut } from '../../types';
import { useUIStore } from '../../store/uiStore';
import { useFolderStore } from '../../store/folderStore';
import { useChatStore } from '../../store/chatStore';
import { apiService } from '../../services/api';
import CreateFolderModal from './CreateFolderModal';
import RenameFolderModal from './RenameFolderModal';
import DeleteFolderModal from './DeleteFolderModal';
import RenameThreadModal from '../chat/RenameThreadModal';
import DeleteThreadModal from '../chat/DeleteThreadModal';
import ConnectorLogo, { hasConnectorLogo } from '../ConnectorLogo';

// Fallback title for threads without a title (makes it easy to debug by searching for this constant)
const THREAD_NO_TITLE_FALLBACK = 'Untitled Thread';

// Mock threads for folders (TODO: Replace with real API data when thread-folder relation is implemented)
const mockFolderThreads: Record<string, Thread[]> = {};

interface FolderItemProps {
  folder: FolderType;
  level: number;
  folderIdToConnectorType?: Record<string, string>;
  onSelect?: (folderId: string) => void;
  onCreateSubfolder?: (parentId: string, parentName?: string) => void;
  onRename?: (folderId: string, folderName: string, folderDescription?: string) => void;
  onDelete?: (folderId: string, folderName: string, hasSubfolders: boolean, documentCount?: number, threadCount?: number) => void;
  onRenameThread?: (threadId: string, title: string) => void;
  onDeleteThread?: (threadId: string, title: string) => void;
}

const FolderItem: React.FC<FolderItemProps> = ({
  folder,
  level,
  folderIdToConnectorType,
  onSelect,
  onCreateSubfolder,
  onRename,
  onDelete,
  onRenameThread,
  onDeleteThread,
}) => {
  const connectorType = folderIdToConnectorType?.[folder.id];
  const showConnectorLogo = connectorType && hasConnectorLogo(connectorType);
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = React.useState(level === 0);
  const [showActions, setShowActions] = React.useState(false);
  const { selectedFolder, setSelectedFolder } = useUIStore();
  const { getThreadsForFolder, fetchThreads, currentThread } = useChatStore();
  const isSelected = selectedFolder === folder.id;
  
  // Get threads for this specific folder
  const threads = getThreadsForFolder(folder.id);
  
  const hasChildren = folder.children && folder.children.length > 0;
  const [contextList, setContextList] = React.useState<FolderContextOut[]>([]);
  const paddingLeft = level * 16 + 8;
  // Always show collapse/expand chevron for every folder (even when it only has threads)
  const showExpand = true;

  // Fetch threads when folder is expanded (keep threads visible as long as folder is open)
  React.useEffect(() => {
    if (isExpanded) {
      fetchThreads(false, folder.id);
    }
  }, [isExpanded, folder.id, fetchThreads]);

  // Fetch context for this folder when expanded (context shown as items under the folder)
  React.useEffect(() => {
    if (isExpanded) {
      apiService.getFolderContext(folder.id).then(setContextList).catch(() => setContextList([]));
    } else {
      setContextList([]);
    }
  }, [isExpanded, folder.id]);

  return (
    <div>
      <div
        className={`group flex items-center justify-between py-0.5 px-2 rounded-md cursor-pointer transition-all duration-200 ${
          isSelected
            ? 'bg-gray-50 border border-blue-200'
            : 'hover:bg-gray-100 border border-transparent'
        }`}
        style={{ paddingLeft }}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
        onClick={() => {
          // Expand this folder when clicked
          if (!isExpanded) {
            setIsExpanded(true);
          }
          onSelect?.(folder.id);
        }}
      >
        {/* Left: chevron + folder icon + name (truncates) */}
        <div className="flex items-center space-x-1 flex-1 min-w-0">
          {showExpand ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="p-0.5 hover:bg-gray-200 rounded flex-shrink-0"
            >
              {isExpanded ? (
                <ChevronDown className="h-3 w-3 text-gray-500" />
              ) : (
                <ChevronRight className="h-3 w-3 text-gray-500" />
              )}
            </button>
          ) : (
            <div className="w-4 flex-shrink-0" />
          )}
          <Folder className={`h-4 w-4 flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
          <span
            className={`text-sm truncate min-w-0 ${
              isSelected ? 'text-blue-900 font-semibold' : 'text-gray-700'
            }`}
            title={folder.name}
          >
            {folder.name}
          </span>
          {folder.document_count !== undefined && folder.document_count > 0 && (
            <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full flex-shrink-0">
              {folder.document_count}
            </span>
          )}
        </div>

        {/* Right: fixed strip — type icon (connector) + actions on hover */}
        <div className="flex items-center justify-end gap-0.5 flex-shrink-0 min-w-[7rem] pl-2">
          {showConnectorLogo && (
            <ConnectorLogo connectorType={connectorType} size={14} className="flex-shrink-0 opacity-80" alt="" />
          )}
          {showActions && (
            <div className="flex items-center space-x-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFolder(folder.id);
                navigate('/chat');
              }}
              className="p-1 hover:bg-purple-200 rounded text-gray-500 hover:text-purple-700"
              title="New thread in this folder"
            >
              <MessagesSquare className="h-3 w-3" />
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/documents/${folder.id}`);
              }}
              className="p-1 hover:bg-blue-200 rounded text-gray-500 hover:text-blue-700"
              title="View documents"
            >
              <FileText className="h-3 w-3" />
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCreateSubfolder?.(folder.id, folder.name);
              }}
              className="p-1 hover:bg-gray-200 rounded text-gray-500 hover:text-gray-700"
              title="Create subfolder"
            >
              <FolderPlus className="h-3 w-3" />
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRename?.(folder.id, folder.name, folder.description);
              }}
              className="p-1 hover:bg-gray-200 rounded text-gray-500 hover:text-gray-700"
              title="Rename folder"
            >
              <Edit2 className="h-3 w-3" />
            </button>
            
            <button
              onClick={async (e) => {
                e.stopPropagation();
                // Ensure threads are fetched for this folder to get accurate count
                await fetchThreads(false, folder.id);
                const folderThreads = getThreadsForFolder(folder.id);
                onDelete?.(folder.id, folder.name, !!hasChildren, folder.document_count || 0, folderThreads.length);
              }}
              className="p-1 hover:bg-red-200 rounded text-gray-500 hover:text-red-700"
              title="Delete folder"
            >
              <Trash2 className="h-3 w-3" />
            </button>
            </div>
          )}
        </div>
      </div>

      {hasChildren && isExpanded && (
        <div>
          {folder.children!.map((child) => (
            <FolderItem
              key={child.id}
              folder={child}
              level={level + 1}
              folderIdToConnectorType={folderIdToConnectorType}
              onSelect={onSelect}
              onCreateSubfolder={onCreateSubfolder}
              onRename={onRename}
              onDelete={onDelete}
              onRenameThread={onRenameThread}
              onDeleteThread={onDeleteThread}
            />
          ))}
        </div>
      )}

      {/* Context items: slight left of actual subfolders; sorted alphabetically by display name */}
      {isExpanded && contextList.length > 0 && (
        <div className="space-y-0 min-w-0">
          {[...contextList]
            .sort((a, b) =>
              (a.display_name ?? (a.context_type === 'folder' ? 'Folder' : 'Data source')).localeCompare(
                b.display_name ?? (b.context_type === 'folder' ? 'Folder' : 'Data source'),
                undefined,
                { sensitivity: 'base' }
              )
            )
            .map((ctx) => (
            <div
              key={ctx.id}
              className="group flex items-center min-w-0 py-0.5 px-2 rounded-md text-gray-500 hover:bg-gray-50 border border-transparent"
              style={{ paddingLeft: (level + 1) * 16 + 12 }}
              title={`${ctx.display_name || (ctx.context_type === 'folder' ? 'Folder' : 'Data source')} — ${ctx.context_type === 'folder' ? 'folder' : 'data source'} (included as context)`}
            >
              <div className="w-4 flex-shrink-0" />
              <Folder className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden />
              <span className="text-sm truncate italic min-w-0 flex-1 ml-1">
                {ctx.display_name || (ctx.context_type === 'folder' ? 'Folder' : 'Data source')}
              </span>
              <div className="flex items-center justify-end gap-0.5 flex-shrink-0 min-w-[7rem] pl-2">
                <Link2 className="h-4 w-4 text-amber-600/80 flex-shrink-0" aria-hidden />
                <button
                  type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await apiService.removeFolderContext(folder.id, ctx.id);
                      const updated = await apiService.getFolderContext(folder.id);
                      setContextList(updated);
                      const { toast } = await import('react-hot-toast');
                      toast.success('Context removed');
                    } catch (err: any) {
                      const { toast } = await import('react-hot-toast');
                      toast.error(err?.response?.data?.detail || err?.message || 'Failed to remove context');
                    }
                  }}
                  className="p-1 hover:bg-red-200 rounded text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  title="Remove context"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Show threads when folder is expanded — indent a bit more so they read as inside the folder */}
      {isExpanded && threads.length > 0 && (
        <div className="ml-6 space-y-0">
          {threads.map((thread) => (
            <ThreadItem 
              key={thread.id} 
              thread={thread} 
              level={level + 1}
              onRename={onRenameThread}
              onDelete={onDeleteThread}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface ThreadItemProps {
  thread: Thread;
  level: number;
  onRename?: (threadId: string, title: string) => void;
  onDelete?: (threadId: string, title: string) => void;
}

const ThreadItem: React.FC<ThreadItemProps> = ({ thread, level, onRename, onDelete }) => {
  const navigate = useNavigate();
  const { setCurrentThread } = useUIStore();
  const { currentThread, setCurrentThread: setChatThread } = useChatStore();
  const paddingLeft = level * 16 + 8;
  const isSelected = currentThread?.id === thread.id;
  const [showActions, setShowActions] = React.useState(false);
  const [showDropdown, setShowDropdown] = React.useState(false);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => setShowDropdown(false);
    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDropdown]);

  const handleThreadClick = () => {
    const threadObj = {
      id: thread.id,
      title: thread.title || THREAD_NO_TITLE_FALLBACK,
      created_at: thread.created_at,
      folder_id: thread.folder_id,
      unread: thread.unread
    };
    setChatThread(threadObj);
    navigate(`/chat/${thread.id}`);
  };

  return (
    <div
      className={`group relative flex items-center justify-between py-0.5 pr-2 rounded-md cursor-pointer transition-all duration-200 ${
        isSelected
          ? 'bg-gray-50 border border-transparent'
          : 'hover:bg-gray-100 border border-transparent'
      }`}
      style={{ paddingLeft }}
      onClick={handleThreadClick}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => { if (!showDropdown) setShowActions(false); }}
    >
      <div className="flex items-center flex-1 min-w-0">
        <MessagesSquare className={`h-3 w-3 mr-2 flex-shrink-0 ${isSelected ? 'text-purple-500' : 'text-gray-400'}`} />
        <span 
          className={`text-sm truncate ${isSelected ? 'text-purple-700' : 'text-gray-700'}`}
          title={thread.title || THREAD_NO_TITLE_FALLBACK}
        >
          {thread.title || THREAD_NO_TITLE_FALLBACK}
        </span>
        {thread.unread && (
          <div className="w-2 h-2 bg-primary-600 rounded-full flex-shrink-0 ml-2" />
        )}
      </div>

      {showActions && onRename && onDelete && (
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDropdown(!showDropdown);
            }}
            className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <MoreHorizontal className="h-3 w-3" />
          </button>

          {showDropdown && (
            <div
              className="absolute right-0 z-10 mt-1 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5"
              onMouseEnter={() => setShowActions(true)}
              onMouseLeave={() => setShowActions(false)}
            >
              <div className="py-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRename(thread.id, thread.title || THREAD_NO_TITLE_FALLBACK);
                    setShowDropdown(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Edit2 className="h-3 w-3 mr-2" />
                  Rename
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(thread.id, thread.title || THREAD_NO_TITLE_FALLBACK);
                    setShowDropdown(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-3 w-3 mr-2" />
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

  const FolderTree: React.FC = () => {
  const navigate = useNavigate();
  const { selectedFolder, setSelectedFolder } = useUIStore();
  const { folders, isLoading, fetchFolders } = useFolderStore();
  const { fetchThreads } = useChatStore();
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [createParentId, setCreateParentId] = React.useState<string | undefined>();
  const [createParentName, setCreateParentName] = React.useState<string | undefined>();
  
  // Rename modal state
  const [showRenameModal, setShowRenameModal] = React.useState(false);
  const [renameFolderId, setRenameFolderId] = React.useState<string | undefined>();
  const [renameFolderName, setRenameFolderName] = React.useState<string | undefined>();
  const [renameFolderDescription, setRenameFolderDescription] = React.useState<string | undefined>();
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [deleteFolderId, setDeleteFolderId] = React.useState<string | undefined>();
  const [deleteFolderName, setDeleteFolderName] = React.useState<string | undefined>();
  const [deleteHasSubfolders, setDeleteHasSubfolders] = React.useState<boolean>(false);
  const [deleteDocumentCount, setDeleteDocumentCount] = React.useState<number>(0);
  const [deleteThreadCount, setDeleteThreadCount] = React.useState<number>(0);

  // Thread rename/delete modal state
  const [showRenameThreadModal, setShowRenameThreadModal] = React.useState(false);
  const [showDeleteThreadModal, setShowDeleteThreadModal] = React.useState(false);
  const [selectedThreadId, setSelectedThreadId] = React.useState<string | undefined>();
  const [selectedThreadTitle, setSelectedThreadTitle] = React.useState<string | undefined>();

  // Map folder id -> connector type for data-source-linked folders (sidebar logos)
  const [folderIdToConnectorType, setFolderIdToConnectorType] = React.useState<Record<string, string>>({});

  // Fetch folders when component mounts
  React.useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  // Fetch data sources to know which folders show a connector logo
  React.useEffect(() => {
    apiService.getDataSources().then((res) => {
      const map: Record<string, string> = {};
      (res.data_sources || []).forEach((ds) => {
        if (ds.folder_id && ds.connector_type) map[ds.folder_id] = ds.connector_type;
      });
      setFolderIdToConnectorType(map);
    }).catch(() => setFolderIdToConnectorType({}));
  }, []);

  const handleSelectFolder = (folderId: string) => {
    // Simply set the selected folder - expansion/thread fetching happens in FolderItem
    // Toggle: unselect if clicking the same folder, otherwise select it
    if (selectedFolder === folderId) {
      setSelectedFolder(undefined);
      navigate('/chat');
    } else {
      setSelectedFolder(folderId);
      navigate(`/folder/${folderId}`); // Navigate to folder view with folder selected
    }
  };

  const handleCreateSubfolder = (parentId: string, parentName?: string) => {
    setCreateParentId(parentId);
    setCreateParentName(parentName);
    setShowCreateModal(true);
  };


  const handleRenameFolder = (folderId: string, folderName: string, folderDescription?: string) => {
    setRenameFolderId(folderId);
    setRenameFolderName(folderName);
    setRenameFolderDescription(folderDescription);
    setShowRenameModal(true);
  };

  const handleDeleteFolder = (folderId: string, folderName: string, hasSubfolders: boolean, documentCount: number = 0, threadCount: number = 0) => {
    setDeleteFolderId(folderId);
    setDeleteFolderName(folderName);
    setDeleteHasSubfolders(hasSubfolders);
    setDeleteDocumentCount(documentCount);
    setDeleteThreadCount(threadCount);
    setShowDeleteModal(true);
  };

  // Thread rename/delete handlers
  const handleRenameThread = (threadId: string, title: string) => {
    setSelectedThreadId(threadId);
    setSelectedThreadTitle(title);
    setShowRenameThreadModal(true);
  };

  const handleDeleteThread = (threadId: string, title: string) => {
    setSelectedThreadId(threadId);
    setSelectedThreadTitle(title);
    setShowDeleteThreadModal(true);
  };

  const closeThreadModals = async () => {
    setShowRenameThreadModal(false);
    setShowDeleteThreadModal(false);
    setSelectedThreadId(undefined);
    setSelectedThreadTitle(undefined);
    // Refresh threads to reflect changes - if folder selected, refresh that folder
    if (selectedFolder) {
      await fetchThreads(false, selectedFolder);
    } else {
      await fetchThreads(true); // Refresh unfiled threads if no folder selected
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-0">
        {folders.length === 0 ? (
          <div className="text-center py-4">
            <Folder className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No folders yet</p>
            <p className="text-xs text-gray-400 mt-1">Use "Create Folder" button above</p>
          </div>
        ) : (
          folders.map((folder) => (
            <FolderItem
              key={folder.id}
              folder={folder}
              level={0}
              folderIdToConnectorType={folderIdToConnectorType}
              onSelect={handleSelectFolder}
              onCreateSubfolder={handleCreateSubfolder}
              onRename={handleRenameFolder}
              onDelete={handleDeleteFolder}
              onRenameThread={handleRenameThread}
              onDeleteThread={handleDeleteThread}
            />
          ))
        )}
      </div>

      <CreateFolderModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        parentId={createParentId}
        parentName={createParentName}
      />

      <RenameFolderModal
        isOpen={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        folderId={renameFolderId}
        currentName={renameFolderName}
        currentDescription={renameFolderDescription}
      />

      <DeleteFolderModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        folderId={deleteFolderId}
        folderName={deleteFolderName}
        hasSubfolders={deleteHasSubfolders}
        documentCount={deleteDocumentCount}
        threadCount={deleteThreadCount}
      />

      {/* Thread Rename/Delete Modals */}
      <RenameThreadModal
        isOpen={showRenameThreadModal}
        onClose={closeThreadModals}
        threadId={selectedThreadId}
        currentTitle={selectedThreadTitle}
      />

      <DeleteThreadModal
        isOpen={showDeleteThreadModal}
        onClose={closeThreadModals}
        threadId={selectedThreadId}
        threadTitle={selectedThreadTitle}
      />
    </>
  );
};

export default FolderTree;
